import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Typography
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import SaveIcon from '@mui/icons-material/Save';
import axios from 'axios';
import TrampolineScorecard from '../components/TrampolineScorecard';
import { getAllowedExercisesByClassName, DEFAULT_EXERCISES } from '../components/scorecarddata/exerciseRules';
import { Exercise } from '../services/competitionCardsService';

// Axios-Instanz ohne Auth-Interceptors für öffentliche Endpunkte
const publicAxios = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000',
  withCredentials: false
});

interface AthleteEntry {
  athlete: {
    _id: string;
    firstname: string;
    surname: string;
    gender: string;
    age: number;
  };
  subdocId: string | null;
  group: {
    groupId: string;
    groupName: string;
    groupType: 'Elite' | 'Basis';
    extraCard?: boolean;
    extraCardCompetitionGroup?: string;
  } | null;
  card: any | null;
  secondCard: any | null;
  exercise: string | null;
}

interface PageData {
  label: string;
  competition: { _id: string; name: string; startDate: string; endDate: string };
  club: { _id: string; name: string };
  expiresAt: string;
  isDeadlinePassed: boolean;
  deadlineBypassed: boolean;
  secondCardOnly: boolean;
  athletes: AthleteEntry[];
}

const CardEditPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<PageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Lokaler Zustand pro Athlet (key = athleteId für Karte 0, athleteId+'_2' für Karte 1)
  const [scorecardData, setScorecardData] = useState<Record<string, any>>({});
  const [exerciseSelections, setExerciseSelections] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState<Record<string, boolean>>({});
  const [saved, setSaved] = useState<Record<string, boolean>>({});
  // Mapping athleteId → subdocId (Subdokument-ID aus CompetitionRegistration)
  const [subdocIds, setSubdocIds] = useState<Record<string, string>>({});

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success'
  });

  useEffect(() => {
    const load = async () => {
      try {
        const res = await publicAxios.get(`/api/card-access/${token}`);
        const pageData: PageData = res.data;
        setData(pageData);

        // Initialen Zustand aus geladenen Daten befüllen
        const initScorecard: Record<string, any> = {};
        const initExercises: Record<string, string> = {};
        const initSubdocIds: Record<string, string> = {};
        pageData.athletes.forEach(entry => {
          if (entry.subdocId) initSubdocIds[entry.athlete._id] = entry.subdocId;
          if (entry.group?.groupType === 'Elite') {
            if (entry.card) initScorecard[entry.athlete._id] = entry.card;
            if (entry.group?.extraCard && entry.secondCard) {
              initScorecard[entry.athlete._id + '_2'] = entry.secondCard;
            }
          }
          if (entry.group?.groupType === 'Basis' && entry.exercise) {
            initExercises[entry.athlete._id] = entry.exercise;
          }
        });
        setScorecardData(initScorecard);
        setExerciseSelections(initExercises);
        setSubdocIds(initSubdocIds);
      } catch (err: any) {
        if (err.response?.status === 410) {
          setError(err.response.data?.message || 'Dieser Link ist abgelaufen oder wurde widerrufen.');
        } else if (err.response?.status === 404) {
          setError('Dieser Link ist ungültig.');
        } else {
          setError('Fehler beim Laden der Daten.');
        }
      } finally {
        setLoading(false);
      }
    };
    if (token) load();
  }, [token]);

  const handleScorecardChange = useCallback((key: string, cardData: any) => {
    setScorecardData(prev => ({ ...prev, [key]: cardData }));
    setSaved(prev => ({ ...prev, [key]: false }));
  }, []);

  const saveCard = async (athleteId: string, cardIndex: number = 0) => {
    const key = cardIndex === 1 ? athleteId + '_2' : athleteId;
    const cardData = scorecardData[key];
    if (!cardData) return;
    setSaving(prev => ({ ...prev, [key]: true }));
    try {
      await publicAxios.post(
        `/api/card-access/${token}/athletes/${athleteId}/card`,
        { ...cardData, cardIndex, subdocId: subdocIds[athleteId] ?? null }
      );
      setSaved(prev => ({ ...prev, [key]: true }));
      setSnackbar({ open: true, message: 'Wettkampfkarte gespeichert.', severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Fehler beim Speichern.', severity: 'error' });
    } finally {
      setSaving(prev => ({ ...prev, [key]: false }));
    }
  };

  const saveExercise = async (athleteId: string) => {
    const exercise = exerciseSelections[athleteId];
    if (!exercise) return;
    setSaving(prev => ({ ...prev, [athleteId]: true }));
    try {
      await publicAxios.post(`/api/card-access/${token}/athletes/${athleteId}/exercise`, { exercise, subdocId: subdocIds[athleteId] ?? null });
      setSaved(prev => ({ ...prev, [athleteId]: true }));
      setSnackbar({ open: true, message: 'Übung gespeichert.', severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Fehler beim Speichern.', severity: 'error' });
    } finally {
      setSaving(prev => ({ ...prev, [athleteId]: false }));
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Typography variant="body2" color="text.secondary">
          Bitte wende dich an deinen Meldeverantwortlichen für einen neuen Link.
        </Typography>
      </Container>
    );
  }

  if (!data) return null;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          Wettkampfkarten bearbeiten
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {data.competition.name} — {data.club.name}
        </Typography>
        {data.label && (
          <Typography variant="body2" color="text.secondary">
            {data.label}
          </Typography>
        )}
        <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <Chip
            label={`Link gültig bis ${new Date(data.expiresAt).toLocaleDateString('de-DE')}`}
            size="small"
            variant="outlined"
          />
          {data.isDeadlinePassed && (
            <Chip
              icon={<WarningIcon />}
              label="Abgabefrist abgelaufen – nur Ansicht"
              color="warning"
              size="small"
            />
          )}
          {data.deadlineBypassed && (
            <Chip
              icon={<WarningIcon />}
              label="Bearbeitung nach Abgabefrist – von Admin freigegeben"
              color="warning"
              size="small"
              variant="outlined"
            />
          )}
        </Box>
      </Paper>

      {data.athletes.length === 0 && (
        <Alert severity="info">Keine Sportler in diesem Link enthalten.</Alert>
      )}

      {data.athletes.map((entry) => {
        const { athlete, group, card, secondCard, exercise } = entry;
        const athleteId = athlete._id;
        const key2 = athleteId + '_2';
        const isElite = group?.groupType === 'Elite';
        const isBasis = group?.groupType === 'Basis';
        const hasExtraCard = isElite && (group?.extraCard ?? false);
        const readOnly = data.isDeadlinePassed;
        const showFirstCard = isElite && !data.secondCardOnly;
        const showSecondCard = hasExtraCard;

        return (
          <Paper key={athleteId} elevation={2} sx={{ mb: 3 }}>
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: '4px 4px 0 0' }}>
              <Typography variant="h6">
                {athlete.firstname} {athlete.surname}
              </Typography>
              {group && (
                <Chip
                  label={`${group.groupName} (${group.groupType})`}
                  size="small"
                  color={isElite ? 'primary' : 'default'}
                  sx={{ mt: 0.5 }}
                />
              )}
            </Box>
            <Divider />

            <Box sx={{ p: 2 }}>
              {!group && (
                <Alert severity="warning">
                  Keine Wettkampfklasse gefunden. Bitte wende dich an den Meldeverantwortlichen.
                </Alert>
              )}

              {/* Erste Karte */}
              {showFirstCard && (
                <Box>
                  {hasExtraCard && (
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                      Karte 1 – {group?.groupName}
                    </Typography>
                  )}
                  <TrampolineScorecard
                    initialData={scorecardData[athleteId] || card || undefined}
                    onChange={(cd) => handleScorecardChange(athleteId, cd)}
                    readOnly={readOnly}
                    competitionGroup={group?.groupName}
                  />
                  {!readOnly && (
                    <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={saving[athleteId] ? <CircularProgress size={16} color="inherit" /> : saved[athleteId] ? <CheckCircleIcon /> : <SaveIcon />}
                        onClick={() => saveCard(athleteId, 0)}
                        disabled={saving[athleteId]}
                        color={saved[athleteId] ? 'success' : 'primary'}
                      >
                        {saved[athleteId] ? 'Gespeichert' : hasExtraCard ? 'Karte 1 speichern' : 'Speichern'}
                      </Button>
                    </Box>
                  )}
                </Box>
              )}

              {/* Zweite Karte (Extra-Karte für Pflichtübung) */}
              {showSecondCard && (
                <Box sx={{ mt: 3 }}>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    Karte 2 – {group?.extraCardCompetitionGroup || 'Zweite Wettkampfkarte'}
                  </Typography>
                  <TrampolineScorecard
                    initialData={scorecardData[key2] || secondCard || undefined}
                    onChange={(cd) => handleScorecardChange(key2, cd)}
                    readOnly={readOnly}
                    competitionGroup={group?.extraCardCompetitionGroup || group?.groupName}
                  />
                  {!readOnly && (
                    <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={saving[key2] ? <CircularProgress size={16} color="inherit" /> : saved[key2] ? <CheckCircleIcon /> : <SaveIcon />}
                        onClick={() => saveCard(athleteId, 1)}
                        disabled={saving[key2]}
                        color={saved[key2] ? 'success' : 'primary'}
                      >
                        {saved[key2] ? 'Gespeichert' : 'Karte 2 speichern'}
                      </Button>
                    </Box>
                  )}
                </Box>
              )}

              {isBasis && (
                <Box sx={{ maxWidth: 400 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Bitte die Pflichtübung für diesen Wettkampf auswählen.
                  </Typography>
                  <FormControl fullWidth size="small" disabled={readOnly}>
                    <InputLabel>Pflichtübung</InputLabel>
                    <Select
                      value={exerciseSelections[athleteId] || ''}
                      label="Pflichtübung"
                      onChange={e => {
                        setExerciseSelections(prev => ({ ...prev, [athleteId]: e.target.value }));
                        setSaved(prev => ({ ...prev, [athleteId]: false }));
                      }}
                    >
                      {(getAllowedExercisesByClassName(group?.groupName || '') || DEFAULT_EXERCISES).map(ex => (
                        <MenuItem key={ex.id} value={ex.id}>{ex.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {!readOnly && (
                    <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={saving[athleteId] ? <CircularProgress size={16} color="inherit" /> : saved[athleteId] ? <CheckCircleIcon /> : <SaveIcon />}
                        onClick={() => saveExercise(athleteId)}
                        disabled={saving[athleteId]}
                        color={saved[athleteId] ? 'success' : 'primary'}
                      >
                        {saved[athleteId] ? 'Gespeichert' : 'Speichern'}
                      </Button>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </Paper>
        );
      })}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default CardEditPage;
